import type {
  Season,
  SeasonConflict,
  SeasonDateRange,
  SeasonRentalAssignment,
} from '@interfaces/seasons.interface';

/**
 * Interval logic for Seasons, kept free of Angular, of the store and of `Date`: every date is a
 * `yyyy-MM-dd` string, which sorts lexicographically, so comparing two days is comparing two
 * strings. That is what makes this file cheap to unit-test and what keeps a timezone from ever
 * shifting a boundary — the caller converts to and from `Date` at the edge, not here.
 *
 * Both ends of a `SeasonDateRange` are inclusive, so two ranges that merely touch on the same day
 * DO overlap: `01-05..01-10` and `01-10..01-15` collide on the 10th.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A pair of ranges inside one season that cover at least one day in common. */
export interface SeasonRangeOverlap {
  first: SeasonDateRange;
  second: SeasonDateRange;
  overlap: SeasonDateRange;
}

/**
 * The season being written, as the overlap check needs it: `id` is `null` while creating and the
 * edited season's id while editing, which is how a season stops colliding with itself.
 */
export interface SeasonConflictDraft {
  id: string | null;
  priority: number;
  ranges: SeasonDateRange[];
  assignment: SeasonRentalAssignment;
}

/**
 * A range is usable when both ends are real `yyyy-MM-dd` days and the end is not before the start.
 * Half-filled ranges fail here too: an empty string sorts before every date, so without the shape
 * check a missing `from` would silently pass as "earlier than everything".
 */
export function rangeIsValid(range: SeasonDateRange): boolean {
  return ISO_DATE.test(range.from) && ISO_DATE.test(range.to) && range.to >= range.from;
}

/**
 * The days two ranges share, or `null` when they share none. The intersection is the later start
 * and the earlier end; it exists only while that pair is still a valid range. An unusable range
 * overlaps nothing — it is reported by `rangeIsValid`, not doubled up as a collision here.
 */
export function overlapOf(a: SeasonDateRange, b: SeasonDateRange): SeasonDateRange | null {
  if (!rangeIsValid(a) || !rangeIsValid(b)) {
    return null;
  }

  const from = a.from > b.from ? a.from : b.from;
  const to = a.to < b.to ? a.to : b.to;

  return from <= to ? { from, to } : null;
}

/**
 * Every pair of ranges within one season that collide, in the order the pairs appear. A range
 * duplicated in the same season is the degenerate case: it intersects itself completely, so it
 * comes back with `overlap` equal to both members of the pair and the caller can tell a repeat from
 * a partial overlap by comparing them.
 */
export function findIntraSeasonOverlaps(ranges: SeasonDateRange[]): SeasonRangeOverlap[] {
  const found: SeasonRangeOverlap[] = [];

  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      const overlap = overlapOf(ranges[i], ranges[j]);
      if (overlap !== null) {
        found.push({ first: ranges[i], second: ranges[j], overlap });
      }
    }
  }

  return found;
}

/**
 * Whether two assignments can ever land on the same rental. `allRentals` on either side follows the
 * host's whole inventory, so it meets anything; otherwise it takes one shared id.
 */
function sharesRental(a: SeasonRentalAssignment, b: SeasonRentalAssignment): boolean {
  if (a.allRentals || b.allRentals) {
    return true;
  }

  const bIds = new Set(b.rentalIds);

  return a.rentalIds.some(id => bIds.has(id));
}

/**
 * The seasons the draft cannot coexist with. Two seasons only fight when they reach the same rental
 * AND carry the same priority: a different priority is not a conflict but the documented way of
 * resolving one — the lower number wins the night. The draft's own id is skipped so editing a
 * season never reports it against itself.
 *
 * Each conflict names the season and carries the exact colliding spans, one per intersecting pair
 * of ranges, because the form has to say WHICH season and WHICH dates — a bare "there is an
 * overlap" is explicitly not acceptable copy.
 */
export function findEqualPriorityConflicts(
  draft: SeasonConflictDraft,
  others: Season[]
): SeasonConflict[] {
  const conflicts: SeasonConflict[] = [];

  for (const other of others) {
    if (other.id === draft.id) {
      continue;
    }
    if (other.priority !== draft.priority) {
      continue;
    }
    if (!sharesRental(draft.assignment, other.assignment)) {
      continue;
    }

    const overlaps: SeasonDateRange[] = [];
    for (const range of draft.ranges) {
      for (const otherRange of other.ranges) {
        const overlap = overlapOf(range, otherRange);
        if (overlap !== null) {
          overlaps.push(overlap);
        }
      }
    }

    if (overlaps.length > 0) {
      conflicts.push({ seasonId: other.id, seasonName: other.name, overlaps });
    }
  }

  return conflicts;
}
